/**
 * @fileoverview Dual Engine Coordinator for Firebase P2P Testing
 *
 * Manages two ZodiacEngine instances that both connect to the same Firebase
 * game document, simulating real P2P gameplay within a single process.
 *
 * NOTE: This uses a simplified approach - no automatic listeners.
 * Instead, we manually sync state after moves to test Firebase round-trip.
 */

import { ZodiacEngine } from "../../zodiacEngine/zodiacEngine";
import { Mode, State, MoveType, IPiece, GameState, PieceType, ISquare } from "../../zodiacEngine/types";
import { ZodiacFEN } from "../../zodiacEngine/Fen/ZodiacFEN";
import { SyncBarrier } from "./SyncBarrier";
import TimeControlManager from "../../timeControl/TimeControlManager";
import {
  DualEngineTestConfig,
  MoveExecutionResult,
  PieceIdChange,
  ConsistencyCheck,
  SyncTiming,
  EngineStateSnapshot,
  extractStateSnapshot,
} from "./FirebaseTestTypes";
import { ParsedMove } from "../types";
import {
  verifyStateConsistency,
  verifyBonusTurnSync,
  trackPieceIdChanges,
} from "./ConsistencyVerifier";
import { AIMoveResult } from "../../zodiacEngine/AI/Core/IAIPlayer";
import { executeAIMove } from "../AIMoveExecutor";

/**
 * Coordinates two ZodiacEngine instances for Firebase P2P testing
 */
export class DualEngineCoordinator {
  private lightEngine: ZodiacEngine;
  private darkEngine: ZodiacEngine;
  private gameId: string = "";
  private syncBarrier!: SyncBarrier;
  private pieceIdChanges: PieceIdChange[] = [];
  private consistencyChecks: ConsistencyCheck[] = [];
  private roundTripCount: number = 0;
  private initialized: boolean = false;
  private config!: DualEngineTestConfig;

  constructor() {
    this.lightEngine = new ZodiacEngine();
    this.darkEngine = new ZodiacEngine();
  }

  /**
   * Initialize both engines with the test configuration
   */
  async initialize(config: DualEngineTestConfig): Promise<string> {
    this.config = config;
    this.gameId = config.testId + "-" + crypto.randomUUID().slice(0, 8);

    // CRITICAL: Reset TimeControlManager to prevent stale timeout state from previous tests
    // Without this, clock tests that expire the timer will cause game over tests to
    // incorrectly trigger timeout victories instead of race/kingslayer victories
    TimeControlManager.reset();

    // Setup both engines identically
    this.setupEngine(this.lightEngine, config);
    this.setupEngine(this.darkEngine, config);

    // CRITICAL: Set game ID on both engines' EventManagers
    // This is required for Live mode to send updates to Firebase
    (this.lightEngine.getEventManager() as any).gameId = this.gameId;
    (this.darkEngine.getEventManager() as any).gameId = this.gameId;

    // CRITICAL: Disable automatic Firebase updates from the engine
    // We manually control sync via syncToFirebase/syncFromFirebase
    // Otherwise, the engine's sendUpdateToRemote races with our manual sync
    (this.lightEngine.getEventManager() as any).disableRemoteSync = true;
    (this.darkEngine.getEventManager() as any).disableRemoteSync = true;

    // Create the sync barrier
    this.syncBarrier = new SyncBarrier(
      this.lightEngine,
      this.darkEngine,
      this.gameId,
      50
    );

    // Create initial game state in Firebase
    await this.createFirebaseGame(config);

    this.initialized = true;
    return this.gameId;
  }

  /**
   * Setup a single engine with the test configuration
   */
  private setupEngine(engine: ZodiacEngine, config: DualEngineTestConfig): void {
    // Set to Live mode
    engine.setGameMode(Mode.Live);

    // Load FEN
    engine.loadFromFEN(config.inputFEN);

    // CRITICAL: Use gameStateManager.getGameState() to get the ACTUAL reference
    // engine.getGameState() returns a SHALLOW COPY, so modifications don't persist!
    const state = (engine as any).gameStateManager.getGameState();

    // Set kings
    state.lightKing = config.lightKing;
    state.darkKing = config.darkKing;

    // Force into Playing state with TestAI mode for deterministic behavior
    state.state = State.Playing;
    state.turnCount = 10;
    state.mode = Mode.TestAI; // Use TestAI for deterministic dice rolls in tests

    // Set player IDs
    state.lightPlayer = config.lightPlayerId;
    state.darkPlayer = config.darkPlayerId;

    // Ensure isLightTurn is set correctly based on FEN
    // If FEN starts with 'd', it's dark's turn
    if (config.inputFEN.startsWith('d')) {
      state.isLightTurn = false;
    } else {
      state.isLightTurn = true;
    }
  }

  /**
   * Create the game in Firebase
   */
  private async createFirebaseGame(config: DualEngineTestConfig): Promise<void> {
    const { updateGamesDocument } = await import("../../firebase/firebase");
    const { convertToFirebaseState } = await import("../../firebase/firebaseConverter");

    // Use gameStateManager to get the actual state (not a shallow copy)
    const gameState = (this.lightEngine as any).gameStateManager.getGameState();

    const firebaseState = convertToFirebaseState(
      gameState,
      false,
      undefined,
      undefined
    );

    await updateGamesDocument(this.gameId, firebaseState);
  }

  /**
   * Execute a move on the specified engine
   */
  async executeMoveOnEngine(
    role: "light" | "dark",
    parsedMove: ParsedMove
  ): Promise<MoveExecutionResult> {
    const engine = role === "light" ? this.lightEngine : this.darkEngine;
    // CRITICAL: Use gameStateManager.getGameState() to get the ACTUAL reference
    // engine.getGameState() returns a SHALLOW COPY, so modifications don't persist!
    const gameState = (engine as any).gameStateManager.getGameState();

    // Capture before state (use engine.getGameState() for snapshot since it makes a copy)
    const beforeFEN = ZodiacFEN.encode(engine.getGameState());
    const beforeSnapshot = extractStateSnapshot(engine.getGameState());

    // Ensure correct turn (now this persists because we have the actual reference)
    gameState.isLightTurn = parsedMove.color === "light";

    // Handle special commands (timeout)
    if (parsedMove.isSpecialCommand && parsedMove.specialCommand === "timeout") {
      engine.triggerTimeout(parsedMove.color);

      // Sync timeout state to Firebase and other engine (same as normal moves)
      this.roundTripCount++;
      await this.syncToFirebase(engine);
      const nonMovingEngine = role === "light" ? this.darkEngine : this.lightEngine;
      await this.syncFromFirebase(nonMovingEngine);

      const afterState = engine.getGameState();
      return {
        success: true,
        beforeFEN,
        afterFEN: ZodiacFEN.encode(afterState),
        beforeSnapshot,
        afterSnapshot: extractStateSnapshot(afterState),
      };
    }

    // Find the piece
    const fromSquare = gameState.board[parsedMove.fromPosition.row]?.[parsedMove.fromPosition.col];
    if (!fromSquare) {
      return {
        success: false,
        error: `Invalid from position: ${parsedMove.fromPosition.row},${parsedMove.fromPosition.col}`,
        beforeFEN,
        afterFEN: beforeFEN,
        beforeSnapshot,
        afterSnapshot: beforeSnapshot,
      };
    }

    // Find the piece - handle zombified pieces
    const piece = fromSquare.Pieces.find((p: IPiece) => {
      if (p.type !== parsedMove.pieceType) return false;
      const effectiveIsLight = p.zombifiedStatus?.isZombified ? !p.isLight : p.isLight;
      return effectiveIsLight === (parsedMove.color === "light");
    });

    if (!piece) {
      return {
        success: false,
        error: `No ${parsedMove.color} ${PieceType[parsedMove.pieceType]} at position`,
        beforeFEN,
        afterFEN: beforeFEN,
        beforeSnapshot,
        afterSnapshot: beforeSnapshot,
      };
    }

    // Get valid moves
    const validMoves = engine.getValidMoves(parsedMove.fromPosition, piece);

    // Find matching move
    let targetMove = validMoves.find((m) => {
      // Handle entangle moves (including split entangle which uses MoveType.regular)
      // Check if this move has an entangle side effect at the target position
      if (parsedMove.moveType === MoveType.noMove || parsedMove.moveType === MoveType.tripleentangle || parsedMove.entangleDirection) {
        const matchingEntangleSE = m.sideEffects?.find((se) => {
          const posMatches =
            se.position.row === parsedMove.toPosition.row &&
            se.position.col === parsedMove.toPosition.col;

          // If direction is specified, also match direction (for split entangle disambiguation)
          if (parsedMove.entangleDirection && (se as any).direction) {
            return posMatches && (se as any).direction === parsedMove.entangleDirection;
          }

          return posMatches;
        });
        // Split entangle uses MoveType.regular, so also allow that
        if (matchingEntangleSE && (m.moveType === MoveType.noMove || m.moveType === MoveType.regular)) {
          return true;
        }
      }

      const toMatches =
        m.position.row === parsedMove.toPosition.row &&
        m.position.col === parsedMove.toPosition.col;

      if (parsedMove.moveType !== MoveType.regular) {
        return toMatches && m.moveType === parsedMove.moveType;
      }

      return toMatches;
    });

    if (!targetMove) {
      const availableMoves = validMoves.map(
        (m) => `(${m.position.row},${m.position.col}):${m.moveType}`
      );
      return {
        success: false,
        error: `No valid move to (${parsedMove.toPosition.row},${parsedMove.toPosition.col}). Available: [${availableMoves.join(", ")}]`,
        beforeFEN,
        afterFEN: beforeFEN,
        beforeSnapshot,
        afterSnapshot: beforeSnapshot,
      };
    }

    // Get target piece for captures/infections
    const toSquare = gameState.board[parsedMove.toPosition.row]?.[parsedMove.toPosition.col];
    let targetPiece: IPiece | null = null;

    if (targetMove.moveType === MoveType.infect) {
      targetPiece = toSquare?.Pieces[0] || null;
    } else if (targetMove.moveType === MoveType.lane || targetMove.moveType === MoveType.house) {
      targetPiece = toSquare?.Pieces.find((p: IPiece) => p.isLight === (parsedMove.color === "light")) || null;
    } else {
      targetPiece = toSquare?.Pieces.find((p: IPiece) => p.isLight !== (parsedMove.color === "light")) || null;
    }

    // Execute the move
    try {
      await engine.makeMove(
        parsedMove.fromPosition,
        targetMove.position,
        piece,
        targetPiece,
        targetMove.moveType,
        targetMove.sideEffects || []
      );

      this.roundTripCount++;

      // After move, sync state to Firebase
      await this.syncToFirebase(engine);

      // CRITICAL: Only sync the NON-moving engine from Firebase
      // The moving engine already has correct state (including removeOnTurn for poison/zombify)
      // Reloading from Firebase would lose this info since FEN doesn't encode expiration turns
      const nonMovingEngine = role === "light" ? this.darkEngine : this.lightEngine;
      await this.syncFromFirebase(nonMovingEngine);

      const afterState = engine.getGameState();
      return {
        success: true,
        beforeFEN,
        afterFEN: ZodiacFEN.encode(afterState),
        beforeSnapshot,
        afterSnapshot: extractStateSnapshot(afterState),
      };
    } catch (error) {
      return {
        success: false,
        error: `Move execution failed: ${error instanceof Error ? error.message : String(error)}`,
        beforeFEN,
        afterFEN: beforeFEN,
        beforeSnapshot,
        afterSnapshot: beforeSnapshot,
      };
    }
  }

  /**
   * Sync engine state to Firebase
   */
  private async syncToFirebase(engine: ZodiacEngine): Promise<void> {
    const { updateGamesDocument } = await import("../../firebase/firebase");
    const { convertToFirebaseState } = await import("../../firebase/firebaseConverter");

    const gameState = engine.getGameState();

    const firebaseState = convertToFirebaseState(
      gameState,
      true, // move made
      undefined,
      undefined
    );

    await updateGamesDocument(this.gameId, firebaseState);
  }

  /**
   * Sync engine state from Firebase
   */
  private async syncFromFirebase(engine: ZodiacEngine): Promise<void> {
    const { getGameStateByGameId } = await import("../../firebase/firebase");
    const { convertFromFirebaseState } = await import("../../firebase/firebaseConverter");

    const data = await getGameStateByGameId(this.gameId);
    if (!data) {
      throw new Error(`Game ${this.gameId} not found in Firebase`);
    }

    const newState = convertFromFirebaseState(data);

    // Track piece ID changes
    const changes = trackPieceIdChanges(engine.getGameState().board, newState.board);
    this.pieceIdChanges.push(...changes);

    // NOTE: Status effect timing (removeOnTurn) is now handled by convertFromFirebaseState
    // via applyStatusEffects() using position-based lookup. No need to preserve/restore here.

    // Apply state but preserve critical fields
    const currentState = engine.getGameState() as any;
    const preservedFields = {
      lightKing: currentState.lightKing,
      darkKing: currentState.darkKing,
      lightPlayer: currentState.lightPlayer,
      darkPlayer: currentState.darkPlayer,
      mode: currentState.mode, // CRITICAL: Preserve mode for valid move generation!
    };

    // Apply new state
    (engine as any).gameStateManager.setGameState(newState);

    // CRITICAL: Use gameStateManager.getGameState() to get the ACTUAL reference
    // engine.getGameState() returns a SHALLOW COPY, so modifications don't persist!
    const updatedState = (engine as any).gameStateManager.getGameState();
    Object.assign(updatedState, preservedFields);

    // Preserve game end state if game has ended, otherwise ensure Playing state
    // CRITICAL: Don't overwrite PostGame/Draw state or result detection will fail!
    const gameHasEnded = newState.state === State.PostGame ||
                         newState.state === State.Draw ||
                         newState.lightWon !== undefined;
    if (!gameHasEnded) {
      updatedState.state = State.Playing;
    } else {
      // Game has ended - preserve the end state and result info
      updatedState.state = newState.state;
      updatedState.lightWon = newState.lightWon;
    }
    updatedState.mode = Mode.Live; // Force Live mode for P2P testing
    if (updatedState.turnCount < 10) {
      updatedState.turnCount = 10; // Skip placement phase for testing
    }

    // CRITICAL: Mark king pieces after sync
    // Firebase stores lightKing/darkKing as piece types, but piece.isKing needs to be set
    this.markKingPieces(updatedState);

    // CRITICAL: Set shadow state so engine knows to remove shadows on next move
    // Without this, shadows from opponent's ink persist when they should be removed
    if (newState.shadowsActive) {
      engine.setShadowsActive(true);
    }
  }

  /**
   * Wait for both engines to sync
   */
  async waitForSyncComplete(): Promise<void> {
    // With manual sync, engines should already be synced
    // Just verify they match
    await this.delay(50);
  }

  /**
   * Wait for bonus turn sync
   */
  async waitForBonusTurnSync(): Promise<{
    skipTurnChange: boolean;
    activePieceId: string | null;
    syncTimeMs: number;
  }> {
    const startTime = performance.now();
    const lightState = this.lightEngine.getGameState();
    return {
      skipTurnChange: lightState.skipTurnChange ?? false,
      activePieceId: lightState.activeBonusTurnPieceId ?? null,
      syncTimeMs: performance.now() - startTime,
    };
  }

  /**
   * Verify state consistency between engines
   */
  verifyConsistency(): ConsistencyCheck[] {
    const checks = verifyStateConsistency(
      this.lightEngine.getGameState(),
      this.darkEngine.getGameState()
    );
    this.consistencyChecks.push(...checks);
    return checks;
  }

  /**
   * Get bonus turn sync status
   */
  getBonusTurnSync() {
    return verifyBonusTurnSync(
      this.lightEngine.getGameState(),
      this.darkEngine.getGameState()
    );
  }

  getLightEngine(): ZodiacEngine { return this.lightEngine; }
  getDarkEngine(): ZodiacEngine { return this.darkEngine; }
  getLightEngineState(): GameState { return this.lightEngine.getGameState(); }
  getDarkEngineState(): GameState { return this.darkEngine.getGameState(); }
  getLightEngineFEN(): string { return ZodiacFEN.encode(this.lightEngine.getGameState()); }
  getDarkEngineFEN(): string { return ZodiacFEN.encode(this.darkEngine.getGameState()); }
  getPieceIdChanges(): PieceIdChange[] { return [...this.pieceIdChanges]; }
  getConsistencyChecks(): ConsistencyCheck[] { return [...this.consistencyChecks]; }
  getSyncTimings(): SyncTiming[] { return this.syncBarrier.getSyncTimings(); }
  getRoundTripCount(): number { return this.roundTripCount; }
  getGameId(): string { return this.gameId; }

  areEnginesConsistent(): boolean {
    const checks = verifyStateConsistency(
      this.lightEngine.getGameState(),
      this.darkEngine.getGameState()
    );
    return checks.every((c) => c.matches);
  }

  /**
   * Execute an AI-generated move on the specified engine and sync via Firebase.
   * Replicates the full BotGameController pipeline:
   * AI move -> piece lookup -> execute -> sync to Firebase -> sync non-moving engine
   */
  async executeAIMoveAndSync(
    role: "light" | "dark",
    moveResult: AIMoveResult,
    gameState: GameState
  ): Promise<MoveExecutionResult> {
    const engine = role === "light" ? this.lightEngine : this.darkEngine;

    // Capture before state
    const beforeFEN = ZodiacFEN.encode(engine.getGameState());
    const beforeSnapshot = extractStateSnapshot(engine.getGameState());

    // Execute AI move using the BotGameController pattern
    const execResult = await executeAIMove(engine, moveResult, gameState);

    if (!execResult.success) {
      return {
        success: false,
        error: execResult.error,
        beforeFEN,
        afterFEN: beforeFEN,
        beforeSnapshot,
        afterSnapshot: beforeSnapshot,
      };
    }

    this.roundTripCount++;

    // Sync to Firebase
    await this.syncToFirebase(engine);

    // Sync non-moving engine from Firebase
    const nonMovingEngine = role === "light" ? this.darkEngine : this.lightEngine;
    await this.syncFromFirebase(nonMovingEngine);

    const afterState = engine.getGameState();
    return {
      success: true,
      beforeFEN,
      afterFEN: ZodiacFEN.encode(afterState),
      beforeSnapshot,
      afterSnapshot: extractStateSnapshot(afterState),
    };
  }

  async cleanup(): Promise<void> {
    try {
      const { deleteGamesDocument } = await import("../../firebase/firebase");
      if (this.gameId) {
        await deleteGamesDocument(this.gameId);
      }
    } catch (error) {
      // Ignore cleanup errors
    }
    this.initialized = false;
  }

  /**
   * Ensure test players have completed placement (isPlaced: true)
   * This prevents all game over tests from showing "placement" modal
   *
   * Call this before tests that need to verify ranked/casual modal behavior
   */
  static async ensureTestPlayersArePlaced(
    lightPlayerId: string = "test-light-player",
    darkPlayerId: string = "test-dark-player"
  ): Promise<void> {
    try {
      const { doc, getDoc, setDoc, updateDoc } = await import("firebase/firestore");
      const { db } = await import("../../firebase/firebaseConfig");

      for (const playerId of [lightPlayerId, darkPlayerId]) {
        const docRef = doc(db, 'users', playerId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          // Update existing profile to be "placed"
          const data = docSnap.data();
          const profile = data.profile || data;

          if (profile.mmr) {
            profile.mmr.isPlaced = true;
            profile.mmr.placementGames = 10;
            profile.mmr.displayRating = profile.mmr.provisionalRating || 1200;

            // Also update time control ratings if they exist
            if (profile.mmr.timeControlRatings) {
              for (const tc of Object.values(profile.mmr.timeControlRatings) as any[]) {
                tc.isPlaced = true;
                tc.placementGames = 10;
              }
            }

            await updateDoc(docRef, { profile });
          }
        } else {
          // Create a new placed profile
          const now = new Date();
          const placedProfile = {
            mmr: {
              provisionalRating: 1200,
              placementGames: 10,
              placementHistory: [],
              displayRating: 1200,
              rankedGames: 0,
              peak: 1200,
              isPlaced: true,
              timeControlRatings: {
                marathon: {
                  rating: 1200,
                  games: 10,
                  peak: 1200,
                  isPlaced: true,
                  placementGames: 10,
                  provisionalRating: 1200
                }
              },
              totalGames: 10,
              createdAt: now,
              colorBalance: 0
            },
            username: playerId,
            lastActive: now
          };

          await setDoc(docRef, {
            uid: playerId,
            profile: placedProfile,
            gamesPlayed: 10,
            createdAt: now,
            lastActive: now,
            accountType: 'test'
          });
        }
      }
    } catch (error) {
      // Don't throw - tests can still run, just might show placement modal
    }
  }

  /**
   * Mark pieces as kings based on lightKing/darkKing state fields
   * Firebase stores king identity separately, so we need to set piece.isKing after sync
   *
   * IMPORTANT: If FEN already has king markers (♛/♕), pieces already have isKing set correctly.
   * We should only mark kings if NO king of that color exists yet (e.g., initial setup).
   * This prevents overwriting correct king markers on shadow/duplicate piece scenarios.
   */
  private markKingPieces(state: GameState): void {
    if (!state.board) return;

    // First pass: check if kings are already marked from FEN (♛/♕ markers)
    let hasLightKing = false;
    let hasDarkKing = false;

    for (let row = 0; row < state.board.length; row++) {
      for (let col = 0; col < state.board[row].length; col++) {
        const square = state.board[row][col];
        if (!square.Pieces) continue;
        for (const piece of square.Pieces) {
          if (piece.isKing && piece.isLight) hasLightKing = true;
          if (piece.isKing && !piece.isLight) hasDarkKing = true;
        }
      }
    }

    // If both kings are already marked from FEN, trust the FEN - don't overwrite!
    if (hasLightKing && hasDarkKing) {
      return;
    }

    // Second pass: only mark kings that are missing (for initial setup scenarios)
    for (let row = 0; row < state.board.length; row++) {
      for (let col = 0; col < state.board[row].length; col++) {
        const square = state.board[row][col];
        if (!square.Pieces) continue;

        for (const piece of square.Pieces) {
          // Skip shadow pieces - they're never kings
          if (piece.isShadow) continue;

          // Only mark if no king of this color exists yet
          if (!hasLightKing && piece.isLight && state.lightKing && piece.type === state.lightKing) {
            piece.isKing = true;
            hasLightKing = true; // Only mark the first one found
          } else if (!hasDarkKing && !piece.isLight && state.darkKing && piece.type === state.darkKing) {
            piece.isKing = true;
            hasDarkKing = true; // Only mark the first one found
          }
        }
      }
    }
  }

  // NOTE: extractStatusEffectExpirations and restoreStatusEffectExpirations removed
  // Status effect timing is now handled by convertFromFirebaseState via applyStatusEffects()
  // using position-based lookup in the Firebase statusEffects data

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
